import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

const BLR_CENTER = [12.9716, 77.5946];

function shadeFor(row) {
  // Solid amber if any high risk in the locality, orange if only medium,
  // slate otherwise. Radius scales with total ad count.
  if (row.high > 0) return { color: "#dc2626", fill: "#dc2626" };
  if (row.medium > 0) return { color: "#f59e0b", fill: "#f59e0b" };
  return { color: "#64748b", fill: "#64748b" };
}

function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

/** Bangalore leadership heatmap. Solo Leaflet map — no react-leaflet. */
function HeatmapMap({ rows }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    if (el._leaflet_id) delete el._leaflet_id;
    const map = L.map(el, { scrollWheelZoom: false, zoomControl: true }).setView(BLR_CENTER, 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    const group = L.layerGroup();
    const bounds = [];
    rows.forEach((r) => {
      const { color, fill } = shadeFor(r);
      const radius = Math.min(30, 8 + Math.sqrt(r.count) * 3);
      const marker = L.circleMarker([r.lat, r.lng], {
        radius, color, fillColor: fill, fillOpacity: 0.5, weight: 2,
      });
      marker.bindTooltip(`${esc(r.name)} · ${r.count}`);
      marker.bindPopup(
        `<div style="min-width:180px;font-family:-apple-system,Segoe UI,sans-serif">
           <div style="font-weight:600;font-size:13px">${esc(r.name)}</div>
           <div style="font-size:11px;color:#64748b;font-family:ui-monospace,monospace;margin-top:2px">
             ${r.count} ads · ${r.high} high · ${r.medium} medium
           </div>
           ${r.obfuscated ? `<div style="font-size:11px;color:#7c2d12">${r.obfuscated} with obfuscation</div>` : ""}
           <a href="/search?q=${encodeURIComponent(r.name)}"
              style="font-size:12px;color:#1d4ed8">Open leads →</a>
         </div>`,
      );
      marker.on("add", () => {
        const elm = marker.getElement();
        if (elm) elm.setAttribute("data-testid", `heatmap-marker-${r.name.replace(/\s+/g, "-")}`);
      });
      group.addLayer(marker);
      bounds.push([r.lat, r.lng]);
    });
    group.addTo(map);
    layerRef.current = group;
    if (bounds.length === 1) map.setView(bounds[0], 12);
    else if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
  }, [rows]);

  return (
    <div
      ref={containerRef}
      data-testid="dashboard-heatmap-map"
      style={{ height: 420, width: "100%" }}
    />
  );
}

export function BangaloreHeatmap() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/locality-heatmap").then(({ data }) => {
      if (alive) setRows(data.localities || []);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <section
      className="border border-slate-200 bg-white rounded-md overflow-hidden"
      data-testid="dashboard-heatmap"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
          <MapPin className="h-4 w-4 text-red-700" /> Bangalore locality heatmap
        </h2>
        <div className="text-[11px] font-mono text-slate-500 flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-red-600" /> high-risk</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> medium</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-slate-500" /> low</span>
        </div>
      </div>
      {loading ? (
        <div className="h-[420px] grid place-items-center text-sm text-slate-500" data-testid="dashboard-heatmap-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading map…
        </div>
      ) : rows.length === 0 ? (
        <div className="h-[420px] grid place-items-center text-sm text-slate-500" data-testid="dashboard-heatmap-empty">
          No mappable localities yet.
        </div>
      ) : (
        <HeatmapMap rows={rows} />
      )}
    </section>
  );
}
